import {
  Money,
  TaxStrategy,
  roundTo2Decimals,
} from '@principle-theorem/accounting';
import {
  type IGetRecordResponse,
  type IPracticeMigration,
  type ITranslationMap,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  Invoice,
  Transaction,
  TransactionOperators,
  getTransactionAmount,
  stafferToNamedDoc,
  toAccountDetails,
} from '@principle-theorem/principle-core';
import {
  IDiscountExtendedData,
  IFeeLineItem,
  IServiceCodeLineItem,
  InvoiceLineItemType,
  InvoiceStatus,
  TransactionProvider,
  TransactionStatus,
  TransactionType,
  type IBasePatient,
  type ICustomLineItem,
  type IInvoice,
  type IManualExtendedData,
  type IPatient,
  type IPatientContactDetails,
  type IPractice,
  type IStaffer,
  type ITransaction,
  type ITreatmentLineItem,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  INamedDocument,
  WithPrimaryKey,
  resolveSequentially,
  isSameRef,
  sortByCreatedAt,
  sortTimestampAsc,
  toMomentTz,
  toNamedDocument,
  toTimestamp,
  type IIdentifiable,
  type WithRef,
  toISODate,
} from '@principle-theorem/shared';
import { compact, first, groupBy, sortBy, sum, uniq, uniqBy } from 'lodash';
import * as moment from 'moment-timezone';
import { PATIENT_RESOURCE_TYPE } from '../../../../destination/entities/patient';
import { resolveMappedCode } from '../../../../mappings/item-codes';
import { ItemCodeResourceMapType } from '../../../../mappings/item-codes-to-xlsx';
import { getPractitionerOrDefault } from '../../../../mappings/staff';
import { TranslationMapHandler } from '../../../../translation-map';
import { OasisPatientDiscount } from '../../../source/entities/patient-discounts';
import { OasisPatientPaymentAdjustment } from '../../../source/entities/patient-payment-adjustments';
import { OasisPatientPayment } from '../../../source/entities/patient-payments';
import { OasisPatientWriteOff } from '../../../source/entities/patient-treatment-write-offs';
import { OasisPatientTreatment } from '../../../source/entities/patient-treatments';

export type PractitionerTransaction = IIdentifiable &
  ITransaction & {
    practitionerRef: DocumentReference<IStaffer>;
    statementNumber?: number;
  };

interface IPatientAccountSummary {
  totalCharged: number;
  totalPaid: number;
  totalOwing: number;
  treatments: IGetRecordResponse<OasisPatientTreatment>[];
  payments: IGetRecordResponse<OasisPatientPayment>[];
  adjustments: IGetRecordResponse<OasisPatientPaymentAdjustment>[];
  discounts: IGetRecordResponse<OasisPatientDiscount>[];
  writeOffs: IGetRecordResponse<OasisPatientWriteOff>[];
  balance: number;
  paymentTransactions: PractitionerTransaction[];
  adjustmentTransactions: PractitionerTransaction[];
  discountTransactions: PractitionerTransaction[];
  writeOffTransactions: PractitionerTransaction[];
}

interface IInvoiceBuildData {
  invoice: IIdentifiable & IInvoice;
  transactions: PractitionerTransaction[];
  practitionerRef: DocumentReference<IStaffer>;
  statementNumbers: number[];
}

interface IStatementAllocationResult {
  invoices: IInvoiceBuildData[];
  unmatchedTransactions: PractitionerTransaction[];
}

export interface IInvoiceBalanceData {
  invoice: IIdentifiable & IInvoice;
  transactions: PractitionerTransaction[];
}

export class OasisInvoiceBuilder {
  accountSummary: IPatientAccountSummary = {
    totalCharged: 0,
    totalPaid: 0,
    totalOwing: 0,
    balance: 0,
    treatments: [],
    payments: [],
    adjustments: [],
    discounts: [],
    writeOffs: [],
    paymentTransactions: [],
    adjustmentTransactions: [],
    discountTransactions: [],
    writeOffTransactions: [],
  };

  constructor(
    public sourceTreatments: IGetRecordResponse<OasisPatientTreatment>[],
    public sourcePayments: IGetRecordResponse<OasisPatientPayment>[],
    public sourceDiscounts: IGetRecordResponse<OasisPatientDiscount>[],
    public sourcePaymentAdjustments: IGetRecordResponse<OasisPatientPaymentAdjustment>[],
    public sourceWriteOffs: IGetRecordResponse<OasisPatientWriteOff>[],
    public patientBalance: number,
    public staff: WithPrimaryKey<ITranslationMap<IStaffer, unknown>>[],
    public practitioners: INamedDocument<IStaffer>[],
    public sourceItemCodes: WithPrimaryKey<
      ITranslationMap<object, ItemCodeResourceMapType>
    >[],
    public translationMap: TranslationMapHandler,
    public migration: WithRef<IPracticeMigration>
  ) {}

  buildAccountSummary(practiceRef: DocumentReference<IPractice>): void {
    const treatments = this.sourceTreatments.filter(
      (item) => !item.data.isDeleted
    );
    const payments = this.sourcePayments.filter((item) => !item.data.isDeleted);
    const adjustments = this.sourcePaymentAdjustments.filter(
      (item) => !item.data.isDeleted
    );
    const discounts = this.sourceDiscounts.filter(
      (item) => !item.data.isDeleted
    );
    const writeOffs = this.sourceWriteOffs.filter(
      (item) => !item.data.isDeleted
    );

    const totalCharged = [
      ...treatments,
      ...payments,
      ...adjustments,
      ...discounts,
      ...writeOffs,
    ].reduce((total, item) => total + (item.data.amount ?? 0), 0);
    const totalPaid = payments.reduce(
      (total, item) => total + (item.data.amount ?? item.data.claimAmount ?? 0),
      0
    );

    const discountTransactions: PractitionerTransaction[] = this.sourceDiscounts
      .filter(
        (sourceDiscount) =>
          !sourceDiscount.data.isDeleted && !sourceDiscount.data.deletedAt
      )
      .filter((sourceDiscount) => !!sourceDiscount.data.amount)
      .map((sourceDiscount) =>
        this.buildDiscountTransaction(sourceDiscount, practiceRef)
      );

    treatments.map((treatment) => {
      discountTransactions.push(
        ...compact([
          this.buildTreamentDiscountTransaction(treatment, practiceRef),
        ])
      );
    });

    const adjustmentTransactions: PractitionerTransaction[] =
      this.sourcePaymentAdjustments
        .filter(
          (sourceAdjustment) =>
            !sourceAdjustment.data.isDeleted && !sourceAdjustment.data.deletedAt
        )
        .filter((sourceAdjustment) => !!sourceAdjustment.data.amount)
        .map((sourceAdjustment) =>
          this.buildAdjustmentDiscountTransaction(sourceAdjustment, practiceRef)
        );

    const writeOffTransactions: PractitionerTransaction[] = this.sourceWriteOffs
      .filter(
        (sourceWriteOff) =>
          !sourceWriteOff.data.isDeleted && !sourceWriteOff.data.deletedAt
      )
      .filter((sourceWriteOff) => !!sourceWriteOff.data.amount)
      .map((sourceWriteOff) => {
        const staffer = getPractitionerOrDefault(
          sourceWriteOff.data.practitionerId.toString(),
          this.staff,
          this.practitioners
        );

        if (!staffer) {
          throw new Error(
            `Failed to resolve staffer: ${sourceWriteOff.data.practitionerId}`
          );
        }

        const extendedData: IDiscountExtendedData = {
          practitionerRef: staffer.ref,
        };
        const amount = sourceWriteOff.data.amount || 0;
        const transaction = {
          ...Transaction.init({
            provider: TransactionProvider.Discount,
            reference: sourceWriteOff.data.id.toString(),
            from: '',
            to: '',
            amount: Money.amount(Math.abs(amount)),
            type:
              amount < 0 ? TransactionType.Outgoing : TransactionType.Incoming,
            description: sourceWriteOff.data.description || 'Write Off',
            createdAt: toTimestamp(
              moment.tz(
                sourceWriteOff.record.translations.appointmentDate,
                this.migration.configuration.timezone
              )
            ),
            extendedData,
            practiceRef,
            status: TransactionStatus.Complete,
          }),
          practitionerRef: staffer.ref,
        };

        return {
          ...transaction,
          uid: sourceWriteOff.data.id.toString(),
          statementNumber: sourceWriteOff.data.statementNumber,
        };
      });

    const paymentTransactions: PractitionerTransaction[] = this.sourcePayments
      .filter(
        (sourcePayment) =>
          !sourcePayment.data.isDeleted && !sourcePayment.data.deletedAt
      )
      .filter(
        (sourcePayment) =>
          sourcePayment.data.amount !== undefined ||
          sourcePayment.data.thirdPartyAmount !== undefined
      )
      .map((sourcePayment) =>
        this.buildPaymentTransaction(sourcePayment, practiceRef)
      );

    this.accountSummary = {
      totalCharged,
      totalPaid,
      totalOwing: roundTo2Decimals(totalCharged - Math.abs(totalPaid)),
      balance: this.patientBalance,
      treatments,
      payments,
      adjustments,
      discounts,
      writeOffs,
      paymentTransactions,
      adjustmentTransactions,
      discountTransactions,
      writeOffTransactions,
    };
  }

  validateAccountSummary(): void {
    const balance = roundTo2Decimals(
      this.accountSummary.totalCharged - Math.abs(this.accountSummary.totalPaid)
    );
    if (balance !== this.accountSummary.totalOwing) {
      throw new Error(
        `Patient balance mismatch: ${balance} !== ${this.accountSummary.totalOwing}`
      );
    }
  }

  /**
   * Pipeline: buildTreatmentInvoices → per-practitioner allocation → fallback spread → status.
   * Practitioner transactions are isolated; orphans without a matching practitioner go to fallback.
   */
  async buildInvoices(
    patient: IBasePatient & IPatientContactDetails,
    practice: WithRef<IPractice>
  ): Promise<IInvoiceBuildData[]> {
    const invoices = await this.buildTreatmentInvoices(patient, practice);
    const invoiceBuildData = invoices.sort((invoiceA, invoiceB) =>
      sortTimestampAsc(invoiceA.invoice.createdAt, invoiceB.invoice.createdAt)
    );

    const transactions = [
      ...this.accountSummary.adjustmentTransactions,
      ...this.accountSummary.discountTransactions,
      ...this.accountSummary.writeOffTransactions,
      ...this.accountSummary.paymentTransactions,
    ].sort((transactionA, transactionB) =>
      sortTimestampAsc(transactionA.createdAt, transactionB.createdAt)
    );

    const invoicePractitioners = uniqBy(
      invoices.map((invoice) => invoice.practitionerRef),
      (practitionerRef) => practitionerRef.path
    );

    const transactionsWithoutPractitionerInvoices = transactions.filter(
      (transaction) =>
        !invoicePractitioners.some((invoicePractitioner) =>
          isSameRef(invoicePractitioner, transaction.practitionerRef)
        )
    );

    let allResults = invoicePractitioners
      .map((practitionerRef) =>
        this._buildPractitionerInvoices(
          invoiceBuildData,
          practitionerRef,
          transactions
        )
      )
      .flat();

    if (transactionsWithoutPractitionerInvoices.length) {
      allResults = this._buildInvoiceData(
        allResults,
        transactionsWithoutPractitionerInvoices
      );
    }

    return allResults.map((invoiceData) => this.setInvoiceStatus(invoiceData));
  }

  buildFinalInvoice(
    invoiceData: IInvoiceBuildData,
    transactionsForInvoice: PractitionerTransaction[],
    remainingTransactions: PractitionerTransaction[]
  ): {
    transactions: PractitionerTransaction[];
    lineItems: ICustomLineItem[];
  } {
    const lineItems = this.buildFinalLineItems(
      remainingTransactions,
      invoiceData
    );

    return {
      lineItems,
      transactions: [
        ...invoiceData.transactions,
        ...transactionsForInvoice,
        ...remainingTransactions,
      ],
    };
  }

  buildFinalLineItems(
    remainingTransactions: PractitionerTransaction[],
    invoiceData: IInvoiceBuildData
  ): ICustomLineItem[] {
    const depositAmount = Money.amount(
      sum(remainingTransactions.map((payment) => getTransactionAmount(payment)))
    );

    if (depositAmount > 0) {
      return [
        {
          uid: `${invoiceData.invoice.uid}-deposit`,
          type: InvoiceLineItemType.Deposit,
          description: 'Overpayment Balance',
          amount: depositAmount,
          tax: 0,
          taxStatus: TaxStrategy.GSTApplicable,
          quantity: 1,
        },
      ];
    }

    return [];
  }

  setInvoiceStatus(invoiceData: IInvoiceBuildData): IInvoiceBuildData {
    const status = this.getInvoiceStatus(
      Invoice.balance(invoiceData.invoice, invoiceData.transactions)
    );
    const lastTransaction = new TransactionOperators(invoiceData.transactions)
      .completed()
      .sort(sortByCreatedAt)
      .reverse()
      .last();

    if (status === InvoiceStatus.Paid && lastTransaction) {
      invoiceData.invoice.paidAt = lastTransaction.createdAt;
    }
    Invoice.updateStatus(
      invoiceData.invoice,
      status,
      invoiceData.invoice.createdAt
    );

    return invoiceData;
  }

  getInvoiceStatus(amountRemaining: number): InvoiceStatus {
    if (amountRemaining <= 0) {
      return InvoiceStatus.Paid;
    }

    if (amountRemaining > 0) {
      return InvoiceStatus.Issued;
    }

    return InvoiceStatus.Draft;
  }

  /**
   * Groups treatments by (statementNumber || appointmentDate) + practitionerId.
   * Returns invoices with empty transactions — allocation happens in _buildPractitionerInvoices.
   */
  async buildTreatmentInvoices(
    patient: IBasePatient & IPatientContactDetails,
    practice: WithRef<IPractice>
  ): Promise<IInvoiceBuildData[]> {
    const groupedTreatments = groupBy(
      this.sourceTreatments.filter(
        (sourceTreatment) =>
          !sourceTreatment.data.isDeleted && !sourceTreatment.data.deletedAt
      ),
      (sourceTreatment) => {
        const groupKey =
          sourceTreatment.data.statementNumber !== undefined
            ? `${sourceTreatment.data.statementNumber.toString()}-${
                sourceTreatment.data.appointmentDate
              }`
            : sourceTreatment.data.appointmentDate;
        return `${groupKey}-${sourceTreatment.data.practitionerId}`;
      }
    );

    const invoices: IInvoiceBuildData[] = await resolveSequentially(
      Object.values(groupedTreatments),
      async (treatments) => {
        const sortedTreatments = sortBy(
          treatments,
          (treatment) => treatment.data.id
        );
        const sourceInvoice = sortedTreatments[0];
        const appointmentDate = toMomentTz(
          sourceInvoice.record.translations.appointmentDate,
          practice.settings.timezone
        );
        const dueDate = toTimestamp(appointmentDate.clone().add(1, 'week'));
        const practitionerRef = getPractitionerOrDefault(
          sourceInvoice.data.billedToPractitionerId.toString(),
          this.staff,
          this.practitioners
        )?.ref;

        if (!practitionerRef) {
          throw new Error(
            `Practitioner not found for treatment: ${sourceInvoice.data.id}, practitionerId: ${sourceInvoice.data.billedToPractitionerId}`
          );
        }

        const invoice = Invoice.init({
          due: dueDate,
          practice: toNamedDocument(practice),
          from: toAccountDetails(practice),
          to: toAccountDetails(patient),
          issuedAt: toTimestamp(appointmentDate),
          reference: sourceInvoice.data.id.toString(),
          createdAt: sourceInvoice.record.translations.createdAt,
        });

        const isOnBehalfOf = sortedTreatments.find(
          (treatment) =>
            treatment.data.patientId?.toString() !== patient.referenceId
        );
        if (isOnBehalfOf && isOnBehalfOf.data.patientId) {
          const fromRef = await this.translationMap.getDestination<IPatient>(
            isOnBehalfOf.data.patientId.toString(),
            PATIENT_RESOURCE_TYPE
          );

          if (fromRef) {
            const childPatient = await Firestore.getDoc(fromRef);
            const onBehalfOf = toAccountDetails(
              childPatient as IBasePatient & IPatientContactDetails
            );
            invoice.to = {
              ...invoice.to,
              onBehalfOf,
            };
          }
        }

        const lineItems: (ITreatmentLineItem | ICustomLineItem)[] =
          sortedTreatments
            .map((treatment) => {
              const staffer = getPractitionerOrDefault(
                treatment.data.billedToPractitionerId.toString(),
                this.staff,
                this.practitioners
              );

              if (!staffer) {
                throw new Error(
                  `Failed to resolve staffer: ${treatment.data.billedToPractitionerId}`
                );
              }

              const serviceCodeLineItems =
                this.getServiceCodeLineItem(treatment);
              if (!serviceCodeLineItems.length) {
                return this.getNonServiceCodeLineItem(treatment, staffer);
              }

              return this.buildTreatmentLineItem(
                treatment,
                staffer,
                serviceCodeLineItems
              );
            })
            .flat();

        const invoiceWithId = {
          ...invoice,
          items: lineItems.flat(),
          uid: sourceInvoice.data.id.toString(),
        };

        const statementNumbers = uniq(
          sortedTreatments
            .map((treatment) => treatment.data.statementNumber)
            .filter((statementNumber) => statementNumber !== undefined)
        );

        return {
          invoice: invoiceWithId,
          transactions: [],
          practitionerRef,
          statementNumbers,
        };
      }
    );

    return invoices;
  }

  buildTreatmentLineItem(
    treatment: IGetRecordResponse<OasisPatientTreatment>,
    staffer: INamedDocument<IStaffer>,
    treatmentItems: IServiceCodeLineItem[]
  ): ITreatmentLineItem[] {
    const amount = Money.amount(treatment.data.amount ?? 0);

    return [
      {
        uid: treatment.data.id.toString(),
        type: InvoiceLineItemType.Treatment,
        treatmentRef: {
          treatmentUuid: treatment.data.id.toString(),
          attributedTo: stafferToNamedDoc(staffer),
        },
        items: treatmentItems,
        description: treatment.data.itemCodeDescription,
        amount: amount < 0 ? 0 : amount,
        tax: 0,
        taxStatus: TaxStrategy.GSTApplicable,
        quantity: 1,
      },
    ];
  }

  getServiceCodeLineItem(
    treatment: IGetRecordResponse<OasisPatientTreatment>
  ): IServiceCodeLineItem[] {
    let treatmentCode = treatment.data.itemCode;
    if (treatmentCode.length === 2) {
      treatmentCode = `0${treatmentCode}`;
    }
    const code = resolveMappedCode(
      this.sourceItemCodes,
      treatmentCode,
      treatmentCode
    );
    if (!code) {
      return [];
    }

    const quantity =
      treatment.data.quantity && treatment.data.quantity > 0
        ? treatment.data.quantity
        : 1;
    const amounts = Money.allocate(
      treatment.data.amount ?? 0,
      Array.from(Array(quantity), () => 1)
    );

    const allAmountsTheSame = uniq(amounts).length === 1;

    if (allAmountsTheSame) {
      const amount = Money.amount(amounts[0]);

      return [
        {
          uid: treatment.data.id.toString(),
          type: InvoiceLineItemType.ServiceCode,
          code: code.code.toString(),
          toothId: treatment.data.toothNumber,
          description: `${code.code.toString()} - ${
            treatment.data.itemCodeDescription
          }`,
          amount: (amount ?? 0) < 0 ? 0 : amount ?? 0,
          tax: treatment.data.gstAmount ?? 0,
          taxStatus: TaxStrategy.GSTApplicable,
          quantity,
        },
      ];
    }

    return amounts.map((dividedAmount, index) => {
      const amount = Money.amount(dividedAmount);

      return {
        uid: `${treatment.data.id.toString()}-${index}`,
        type: InvoiceLineItemType.ServiceCode,
        code: code.code.toString(),
        toothId: treatment.data.toothNumber,
        description: `${code.code.toString()} - ${
          treatment.data.itemCodeDescription
        }`,
        amount: (amount ?? 0) < 0 ? 0 : amount ?? 0,
        tax: treatment.data.gstAmount ?? 0,
        taxStatus: TaxStrategy.GSTApplicable,
        quantity: 1,
      };
    });
  }

  getNonServiceCodeLineItem(
    treatment: IGetRecordResponse<OasisPatientTreatment>,
    staffer: INamedDocument<IStaffer>
  ): IFeeLineItem[] {
    const quantity =
      treatment.data.quantity && treatment.data.quantity > 0
        ? treatment.data.quantity
        : 1;
    const amount = Money.amount((treatment.data.amount ?? 0) / quantity);

    return [
      {
        uid: treatment.data.id.toString(),
        type: InvoiceLineItemType.Fee,
        description: `${treatment.data.itemCode} - ${treatment.data.itemCodeDescription}`,
        amount: (amount ?? 0) < 0 ? 0 : amount ?? 0,
        tax: treatment.data.gstAmount ?? 0,
        taxStatus: TaxStrategy.GSTApplicable,
        quantity,
        attributedTo: staffer,
      },
    ];
  }

  buildPaymentTransaction(
    sourcePayment: IGetRecordResponse<OasisPatientPayment>,
    practiceRef: DocumentReference<IPractice>
  ): PractitionerTransaction {
    const staffer = getPractitionerOrDefault(
      sourcePayment.data.billedToPractitionerId.toString(),
      this.staff,
      this.practitioners
    );
    if (!staffer) {
      throw new Error(
        `Failed to resolve staffer: ${sourcePayment.data.billedToPractitionerId}`
      );
    }

    const extendedData: IManualExtendedData = {};
    const amount =
      sourcePayment.data.amount ?? sourcePayment.data.thirdPartyAmount ?? 0;
    const provider = sourcePayment.data.cash
      ? TransactionProvider.Cash
      : TransactionProvider.Manual;

    return {
      ...Transaction.init({
        uid: sourcePayment.data.id.toString(),
        provider,
        reference: sourcePayment.data.id.toString(),
        from: '',
        to: '',
        amount: Money.amount(Math.abs(amount)),
        type: amount < 0 ? TransactionType.Outgoing : TransactionType.Incoming,
        description: sourcePayment.data.paymentDescription || 'Payment',
        createdAt: toTimestamp(
          moment.tz(
            sourcePayment.record.translations.appointmentDate,
            this.migration.configuration.timezone
          )
        ),
        extendedData,
        practiceRef,
        status: TransactionStatus.Complete,
      }),
      practitionerRef: staffer.ref,
      statementNumber: sourcePayment.data.statementNumber,
    };
  }

  buildDiscountTransaction(
    sourceDiscount: IGetRecordResponse<OasisPatientDiscount>,
    practiceRef: DocumentReference<IPractice>
  ): PractitionerTransaction {
    const staffer = getPractitionerOrDefault(
      sourceDiscount.data.billedToPractitionerId.toString(),
      this.staff,
      this.practitioners
    );

    if (!staffer) {
      throw new Error(
        `Failed to resolve staffer: ${sourceDiscount.data.billedToPractitionerId}`
      );
    }

    const extendedData: IDiscountExtendedData = {
      practitionerRef: staffer.ref,
    };
    const amount = sourceDiscount.data.amount || 0;
    return {
      ...Transaction.init({
        uid: sourceDiscount.data.id.toString(),
        provider: TransactionProvider.Discount,
        reference: sourceDiscount.data.id.toString(),
        from: '',
        to: '',
        amount: Money.amount(Math.abs(amount)),
        type: amount > 0 ? TransactionType.Incoming : TransactionType.Outgoing,
        description: sourceDiscount.data.description || 'Discount',
        createdAt: toTimestamp(
          moment.tz(
            sourceDiscount.record.translations.appointmentDate,
            this.migration.configuration.timezone
          )
        ),
        extendedData,
        practiceRef,
        status: TransactionStatus.Complete,
      }),
      practitionerRef: staffer.ref,
      statementNumber: sourceDiscount.data.statementNumber,
    };
  }

  buildAdjustmentDiscountTransaction(
    sourceAdjustment: IGetRecordResponse<OasisPatientPaymentAdjustment>,
    practiceRef: DocumentReference<IPractice>
  ): PractitionerTransaction {
    const staffer = getPractitionerOrDefault(
      sourceAdjustment.data.billedToPractitionerId.toString(),
      this.staff,
      this.practitioners
    );

    if (!staffer) {
      throw new Error(
        `Failed to resolve staffer: ${sourceAdjustment.data.billedToPractitionerId}`
      );
    }

    const extendedData: IDiscountExtendedData = {
      practitionerRef: staffer.ref,
    };
    const amount = sourceAdjustment.data.amount || 0;
    return {
      ...Transaction.init({
        uid: sourceAdjustment.data.id.toString(),
        provider: TransactionProvider.Discount,
        reference: sourceAdjustment.data.id.toString(),
        from: '',
        to: '',
        amount: Money.amount(Math.abs(amount)),
        type: amount < 0 ? TransactionType.Incoming : TransactionType.Outgoing,
        description: sourceAdjustment.data.description || 'Adjustment',
        createdAt: toTimestamp(
          moment.tz(
            sourceAdjustment.record.translations.appointmentDate,
            this.migration.configuration.timezone
          )
        ),
        extendedData,
        practiceRef,
        status: TransactionStatus.Complete,
      }),
      practitionerRef: staffer.ref,
      statementNumber: sourceAdjustment.data.statementNumber,
    };
  }

  buildTreamentDiscountTransaction(
    sourceTreatment: IGetRecordResponse<OasisPatientTreatment>,
    practiceRef: DocumentReference<IPractice>
  ): PractitionerTransaction | undefined {
    const amount = Money.amount(sourceTreatment.data.amount ?? 0);
    const staffer = getPractitionerOrDefault(
      sourceTreatment.data.billedToPractitionerId.toString(),
      this.staff,
      this.practitioners
    );

    if (!staffer) {
      throw new Error(
        `Failed to resolve staffer: ${sourceTreatment.data.billedToPractitionerId}`
      );
    }

    if (amount >= 0) {
      return;
    }

    const extendedData: IDiscountExtendedData = {
      practitionerRef: staffer.ref,
    };
    return {
      ...Transaction.init({
        uid: sourceTreatment.data.id.toString(),
        provider: TransactionProvider.Discount,
        reference: sourceTreatment.data.id.toString(),
        from: '',
        to: '',
        amount: Money.amount(Math.abs(amount)),
        type: TransactionType.Incoming,
        description: sourceTreatment.data.itemCodeDescription || 'Discount',
        createdAt: toTimestamp(
          moment.tz(
            sourceTreatment.record.translations.appointmentDate,
            this.migration.configuration.timezone
          )
        ),
        extendedData,
        practiceRef,
        status: TransactionStatus.Complete,
      }),
      practitionerRef: staffer.ref,
      statementNumber: sourceTreatment.data.statementNumber,
    };
  }

  /**
   * Greedy sequential allocation — each invoice consumes transactions until balance satisfied.
   * Last invoice absorbs all remaining (deposit line item for overpayment).
   */
  private _buildInvoiceData(
    invoiceBuildData: IInvoiceBuildData[],
    transactions: PractitionerTransaction[]
  ): IInvoiceBuildData[] {
    const results: IInvoiceBuildData[] = [];

    invoiceBuildData.map((invoiceData, index) => {
      const [transactionsForInvoice, remainingTransactions] =
        getTransactionsForBalance(transactions, invoiceData);
      transactions = remainingTransactions;

      const isLastInvoice = index === invoiceBuildData.length - 1;
      if (!isLastInvoice) {
        results.push({
          invoice: invoiceData.invoice,
          transactions: [
            ...invoiceData.transactions,
            ...transactionsForInvoice,
          ],
          practitionerRef: invoiceData.practitionerRef,
          statementNumbers: invoiceData.statementNumbers,
        });
        return;
      }

      const finalInvoiceData = this.buildFinalInvoice(
        invoiceData,
        transactionsForInvoice,
        remainingTransactions
      );
      invoiceData.invoice.items.push(...finalInvoiceData.lineItems);

      results.push({
        invoice: invoiceData.invoice,
        transactions: finalInvoiceData.transactions,
        practitionerRef: invoiceData.practitionerRef,
        statementNumbers: invoiceData.statementNumbers,
      });
    });

    return results;
  }

  /**
   * Two-pass allocation: statement-match first, then sequential greedy for unmatched.
   */
  private _buildPractitionerInvoices(
    invoiceBuildData: IInvoiceBuildData[],
    practitionerRef: DocumentReference<IStaffer>,
    transactions: PractitionerTransaction[]
  ): IInvoiceBuildData[] {
    const practitionerInvoices = invoiceBuildData.filter((invoiceData) =>
      isSameRef(invoiceData.practitionerRef, practitionerRef)
    );
    const practitionerTransactions = transactions.filter((transaction) =>
      isSameRef(transaction.practitionerRef, practitionerRef)
    );

    const { invoices: invoicesAfterStatementMatch, unmatchedTransactions } =
      this._allocateByStatementNumber(
        practitionerInvoices,
        practitionerTransactions
      );

    return this._buildInvoiceData(
      invoicesAfterStatementMatch,
      // oldest first for sequential allocation
      unmatchedTransactions.sort(sortByCreatedAt).reverse()
    );
  }

  /**
   * Matches transactions to invoices by overlapping statement numbers (capped at balance).
   * Orphan healing: transactions with statementNumber=undefined are healed to match
   * an invoice with the same practitioner and issuedAt timestamp.
   * Invoices without statementNumbers are skipped (handled in _buildInvoiceData).
   */
  private _allocateByStatementNumber(
    invoices: IInvoiceBuildData[],
    transactions: PractitionerTransaction[]
  ): IStatementAllocationResult {
    let unmatchedTransactions = transactions.map((transaction) => {
      if (transaction.statementNumber !== undefined) {
        return transaction;
      }
      const matchingInvoice = invoices.find(
        (invoice) =>
          invoice.statementNumbers.length > 0 &&
          isSameRef(invoice.practitionerRef, transaction.practitionerRef) &&
          invoice.invoice.issuedAt &&
          toISODate(invoice.invoice.issuedAt) ===
            toISODate(transaction.createdAt)
      );
      return matchingInvoice
        ? {
            ...transaction,
            statementNumber: matchingInvoice.statementNumbers[0],
          }
        : transaction;
    });

    const allocatedInvoices = invoices.map((invoice) => {
      if (!invoice.statementNumbers.length) {
        return invoice;
      }

      const [statementMatched, nonStatementMatched] =
        partitionByStatementNumber(
          unmatchedTransactions,
          invoice.statementNumbers
        );

      if (!statementMatched.length) {
        return invoice;
      }

      const [allocated, overflowTransactions] = getTransactionsForBalance(
        statementMatched,
        invoice
      );

      unmatchedTransactions = [...overflowTransactions, ...nonStatementMatched];

      return {
        ...invoice,
        transactions: [...invoice.transactions, ...allocated],
      };
    });

    return { invoices: allocatedInvoices, unmatchedTransactions };
  }
}

/**
 * Allocates transactions sequentially up to invoice balance. Splits oversized transactions.
 */
export function getTransactionsForBalance(
  transactions: PractitionerTransaction[],
  invoiceData: IInvoiceBalanceData
): [PractitionerTransaction[], PractitionerTransaction[]] {
  const result: PractitionerTransaction[] = [];
  const additionalTransactions: PractitionerTransaction[] = [];

  let bestBalance: {
    balance: number;
    index: number;
  } = {
    balance: Invoice.balance(invoiceData.invoice, invoiceData.transactions),
    index: 0,
  };

  let remainingBalance = bestBalance.balance;
  let finalIndex = 0;

  if (bestBalance.balance <= 0) {
    return [result, transactions];
  }

  transactions.map((_transaction, index) => {
    const balance = Invoice.balance(invoiceData.invoice, [
      ...invoiceData.transactions,
      ...transactions.slice(0, index + 1),
    ]);

    if (
      (bestBalance.balance >= 0 && balance <= bestBalance.balance) ||
      (bestBalance.balance < 0 && balance >= bestBalance.balance)
    ) {
      bestBalance = {
        balance,
        index,
      };
    }
  });

  transactions.map((transaction, index) => {
    if (remainingBalance <= 0) {
      return;
    }

    const transactionAmount = getTransactionAmount(transaction);
    if (transactionAmount < remainingBalance) {
      result.push(transaction);
      remainingBalance -= transactionAmount;
      finalIndex = index;
      return;
    }
    if (transactionAmount > remainingBalance) {
      const balanceTransactions = splitTransaction(
        transaction,
        remainingBalance
      );
      result.push(...compact([first(balanceTransactions)]));
      finalIndex = index;
      additionalTransactions.push(...balanceTransactions.slice(1));
      remainingBalance = 0;
      return;
    }
    if (transactionAmount === remainingBalance) {
      result.push(transaction);
      remainingBalance = 0;
      finalIndex = index;
      return;
    }
  });
  transactions.splice(0, finalIndex + 1, ...additionalTransactions);
  return [result, transactions];
}

/**
 * Splits a transaction at requiredAmount into uid-1 and uid-2 portions.
 * Returns [transaction] unchanged if remainder <= epsilon (0.005).
 */
export function splitTransaction(
  transaction: PractitionerTransaction,
  requiredAmount: number
): PractitionerTransaction[] {
  const transactions: PractitionerTransaction[] = [];
  const transactionRemainder = transaction.amount - requiredAmount;

  // Remainders can be produced as epsilon values by floating point division/allocation.
  // We treat small remainders as zero and skip splitting,
  // which would otherwise result in $0.00 transactions in the UI
  if (transactionRemainder <= 0.005) {
    return [transaction];
  }

  transactions.push({
    ...transaction,
    amount: requiredAmount,
    reference: `${transaction.reference}-${transactions.length + 1}`,
    uid: `${transaction.uid}-${transactions.length + 1}`,
  });
  transactions.push({
    ...transaction,
    amount: transactionRemainder,
    reference: `${transaction.reference}-${transactions.length + 1}`,
    uid: `${transaction.uid}-${transactions.length + 1}`,
  });
  return transactions;
}

/**
 * Partitions transactions by whether their statementNumber is in the given list.
 */
export function partitionByStatementNumber(
  transactions: PractitionerTransaction[],
  statementNumbers: number[]
): [PractitionerTransaction[], PractitionerTransaction[]] {
  const statementSet = new Set(statementNumbers);

  const isStatementMatch = (transaction: PractitionerTransaction): boolean =>
    transaction.statementNumber !== undefined &&
    statementSet.has(transaction.statementNumber);

  return [
    transactions.filter(isStatementMatch),
    transactions.filter((transaction) => !isStatementMatch(transaction)),
  ];
}
